import { createContext, useContext } from 'react';
import { RunTrace } from '@/reports/runs';
import { FileRef } from '@/reports/inputs';

/** What every input on the fill page shares: whether it can be edited, where files go, and the captures read so far. */
export interface FillContextValue {
  editable: boolean;
  /** a sheet shown to its author rather than to someone filling it in: not editable, but not locked either */
  preview?: boolean;
  /** upload a file against the report: the reference to store, or null when it failed */
  upload: (file: File, onProgress?: (percent: number) => void) => Promise<FileRef | null>;
  /** each waveform the server has read, by trace key */
  traces: Record<string, RunTrace>;
}

const FillContext = createContext<FillContextValue>({ editable: false, upload: async () => null, traces: {} });

export const FillContextProvider = FillContext.Provider;

export function useFill(): FillContextValue {
  return useContext(FillContext);
}

/** The element id an input's field carries, for the progress rail to scroll to. */
export function inputAnchor(inputId: string): string {
  return `input-${inputId}`;
}

/** Scroll to an input, or one sweep cell's input, and put the cursor in it. */
export function focusSlot(slot: { input: string; cell?: string; field?: string }): void {
  const field = document.getElementById(inputAnchor(slot.input));
  if (!field) return;
  const cell = slot.cell !== undefined ? field.querySelector<HTMLElement>(`[data-cell="${CSS.escape(slot.cell)}"][data-field="${CSS.escape(slot.field ?? '')}"]`) : null;
  const target = cell ?? field;
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const focusable = cell ?? field.querySelector<HTMLElement>('input, textarea, button, [tabindex]');
  window.setTimeout(() => focusable?.focus({ preventScroll: true }), 250);
}
